import type { Sim } from '@/types'

import { palette, rgb } from '@/constants/palette'

export const createInterference = (): Sim => {
    let time = 0
    let gridWidth = 0
    let gridHeight = 0
    let field: HTMLCanvasElement | null = null
    let fieldContext: CanvasRenderingContext2D | null = null
    let image: ImageData | null = null

    const sources = (width: number, height: number) => {
        const separation = 0.17 + 0.07 * Math.sin(time * 0.35)

        return {
            leftX: width * (0.5 - separation / 2),
            rightX: width * (0.5 + separation / 2),
            y: height * 0.5
        }
    }

    return {
        resize: (width: number, height: number) => {
            gridWidth = Math.max(60, Math.min(230, Math.round(width / 2.4)))
            gridHeight = Math.max(40, Math.round((gridWidth * height) / width))
            field = document.createElement('canvas')
            field.width = gridWidth
            field.height = gridHeight
            fieldContext = field.getContext('2d')
            image = fieldContext ? fieldContext.createImageData(gridWidth, gridHeight) : null
        },

        update: (dt: number) => {
            time += dt
        },

        draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => {
            if (!field || !fieldContext || !image) return

            const data = image.data
            const { leftX, rightX, y } = sources(gridWidth, gridHeight)
            const wavelength = gridWidth * 0.072
            const k = (Math.PI * 2) / wavelength
            const omega = Math.PI * 2 * 0.85
            const falloff = gridWidth * 0.09
            const phase = omega * time
            const [br, bg, bb] = rgb.brand
            const [lr, lg, lb] = rgb.lavender

            let pointer = 0

            for (let row = 0; row < gridHeight; row++) {
                const dy = row - y
                const dySquared = dy * dy

                for (let column = 0; column < gridWidth; column++) {
                    const dx1 = column - leftX
                    const dx2 = column - rightX
                    const r1 = Math.sqrt(dx1 * dx1 + dySquared)
                    const r2 = Math.sqrt(dx2 * dx2 + dySquared)
                    const wave = Math.sin(k * r1 - phase) / Math.sqrt(1 + r1 / falloff) + Math.sin(k * r2 - phase) / Math.sqrt(1 + r2 / falloff)
                    const value = Math.max(-1, Math.min(1, wave / 1.6))

                    if (value > 0) {
                        const mix = value * 0.88

                        data[pointer] = 255 + (br - 255) * mix
                        data[pointer + 1] = 255 + (bg - 255) * mix
                        data[pointer + 2] = 255 + (bb - 255) * mix
                    }
                    else {
                        const mix = -value * 0.9

                        data[pointer] = 255 + (lr - 255) * mix
                        data[pointer + 1] = 255 + (lg - 255) * mix
                        data[pointer + 2] = 255 + (lb - 255) * mix
                    }

                    data[pointer + 3] = 255
                    pointer += 4
                }
            }

            fieldContext.putImageData(image, 0, 0)

            ctx.save()
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'
            ctx.drawImage(field, 0, 0, width, height)

            const screen = sources(width, height)

            ctx.font = '500 10px ui-monospace, SFMono-Regular, Menlo, monospace'
            ctx.textAlign = 'center'

            const markers: [number, string][] = [[screen.leftX, 'S1'], [screen.rightX, 'S2']]

            for (const [sourceX, name] of markers) {
                ctx.fillStyle = palette.paper
                ctx.beginPath()
                ctx.arc(sourceX, screen.y, 6, 0, Math.PI * 2)
                ctx.fill()

                ctx.fillStyle = palette.ink
                ctx.beginPath()
                ctx.arc(sourceX, screen.y, 3.5, 0, Math.PI * 2)
                ctx.fill()
                ctx.fillText(name, sourceX, screen.y - 12)
            }

            ctx.restore()
        }
    }
}
